import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const BrandSettingsContext = createContext(null);

const DEFAULT_SETTINGS = {
  tagline: 'KaiCheckIt - before you believe it!',
  tagline_style: {
    color_light: '#334155',
    color_dark: '#cbd5e1',
    font_weight: 'medium'
  },
  logos: {
    default: null,
    true: null,
    mixed: null,
    false: null,
    badge_widget: null
  },
  payment_urls: {
    pro: {
      monthly: null,
      annual: null
    }
  },
  // Full plan objects from admin config (drives PricingPage rendering).
  // Each entry: { plan_id, plan_name, monthly_url, annual_url, is_active,
  //   price, period, badge, features[], limitations[], fine_print, tier, display_order }
  payment_plans: []
};

const CACHE_KEY = '_kai_brand_settings_cache_v1';

// Read cached brand settings from localStorage synchronously on initial render so the
// Shield fallback doesn't flash on subsequent page loads. Only merges the fields we
// care about (tagline, tagline_style, logos) — never trusts cached payment_urls.
const readCachedSettings = () => {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const cached = JSON.parse(raw);
    return {
      ...DEFAULT_SETTINGS,
      tagline: cached.tagline || DEFAULT_SETTINGS.tagline,
      tagline_style: {
        ...DEFAULT_SETTINGS.tagline_style,
        ...(cached.tagline_style || {})
      },
      logos: {
        ...DEFAULT_SETTINGS.logos,
        ...(cached.logos || {})
      }
    };
  } catch (e) {
    return DEFAULT_SETTINGS;
  }
};

const writeCachedSettings = (partial) => {
  if (typeof window === 'undefined') return;
  try {
    const existing = window.localStorage.getItem(CACHE_KEY);
    const merged = existing ? { ...JSON.parse(existing), ...partial } : partial;
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(merged));
  } catch (e) {
    // Quota exceeded or JSON error — non-fatal
  }
};

export const BrandSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(readCachedSettings);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadBrandSettings();
    loadPaymentSettings();
  }, []);

  const loadBrandSettings = async () => {
    try {
      const response = await apiService.request('/brand-settings');
      const nextBrand = {
        tagline: response.tagline || DEFAULT_SETTINGS.tagline,
        tagline_style: {
          color_light: response.tagline_style?.color_light || DEFAULT_SETTINGS.tagline_style.color_light,
          color_dark: response.tagline_style?.color_dark || DEFAULT_SETTINGS.tagline_style.color_dark,
          font_weight: response.tagline_style?.font_weight || DEFAULT_SETTINGS.tagline_style.font_weight
        },
        logos: {
          default: response.logos?.default || null,
          true: response.logos?.true || response.logos?.true_verdict || null,
          mixed: response.logos?.mixed || null,
          false: response.logos?.false || response.logos?.false_verdict || null,
          badge_widget: response.logos?.badge_widget || null
        }
      };
      setSettings(prev => ({ ...prev, ...nextBrand }));
      // Persist to localStorage so subsequent page loads render the brand image
      // synchronously (no flash of the Shield fallback).
      writeCachedSettings(nextBrand);
    } catch (error) {
      console.error('Error loading brand settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPaymentSettings = async () => {
    try {
      const response = await apiService.request('/payment-settings');
      if (response.plans && response.plans.length > 0) {
        const paymentUrls = {};
        const activePlans = response.plans
          .filter(p => p.is_active)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        response.plans.forEach(plan => {
          if (plan.is_active) {
            paymentUrls[plan.plan_id] = {
              monthly: plan.monthly_url || null,
              annual: plan.annual_url || null
            };
          }
        });
        setSettings(prev => ({
          ...prev,
          payment_urls: paymentUrls,
          payment_plans: activePlans
        }));
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
    }
  };

  const refreshSettings = () => {
    loadBrandSettings();
    loadPaymentSettings();
  };

  // Get payment URL for a plan
  const getPaymentUrl = (planId, billingCycle = 'monthly') => {
    const planUrls = settings.payment_urls?.[planId];
    if (!planUrls) {
      return null;
    }
    const url = billingCycle === 'annual' ? planUrls.annual : planUrls.monthly;
    return url;
  };

  // Classify verdict into 'true' | 'false' | 'mixed' bucket.
  // Supports legacy (Fact/Pseudo Fact/False) AND new (Verified/Inconclusive/Debunked) labels.
  const getVerdictClass = (verdict) => {
    if (!verdict) return 'mixed';
    const v = verdict.trim().toLowerCase();
    if (v === 'fact' || v === 'true' || v === 'verified' || v === 'confirmed') return 'true';
    if (v === 'false' || v === 'debunked' || v === 'not verified' || v === 'misleading' || v === 'fake') return 'false';
    return 'mixed';
  };
  
  // Get logo based on verdict — supports BOTH legacy (Fact/Pseudo Fact/False) AND
  // new admin-configured labels (Verified/Inconclusive/Debunked/etc.)
  const getLogoForVerdict = (verdict) => {
    if (!verdict) return settings.logos.default;
    const cls = getVerdictClass(verdict);
    if (cls === 'true') return settings.logos.true || settings.logos.default;
    if (cls === 'false') return settings.logos.false || settings.logos.default;
    return settings.logos.mixed || settings.logos.default;
  };

  // Get font weight as CSS value
  const getFontWeightValue = () => {
    const weight = settings.tagline_style.font_weight;
    switch (weight) {
      case 'normal': return 400;
      case 'medium': return 500;
      case 'semibold': return 600;
      case 'bold': return 700;
      default: return 500;
    }
  };

  return (
    <BrandSettingsContext.Provider value={{
      ...settings,
      isLoading,
      refreshSettings,
      getLogoForVerdict,
      getVerdictClass,
      getFontWeightValue,
      getPaymentUrl
    }}>
      {children}
    </BrandSettingsContext.Provider>
  );
};

export const useBrandSettings = () => {
  const context = useContext(BrandSettingsContext);
  if (!context) {
    // Return defaults if not within provider
    return {
      ...DEFAULT_SETTINGS,
      isLoading: false,
      refreshSettings: () => {},
      getLogoForVerdict: () => null,
      getVerdictClass: () => 'mixed',
      getFontWeightValue: () => 500,
      getPaymentUrl: () => null
    };
  }
  return context;
};

export default BrandSettingsContext;
